import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useAuth} from '../../context/useAuth';
import {activateMessengerIntegrationForAgent, getAgentBasic} from '../../services/DataService';
import {useAppDispatch} from '../../store/hooks';
import {setSnackbarOpen} from '../../store/slices/snackbar.slice';
import type {AgentBasicDto} from '../../dto/AgentBasicDto';

export function MessengerActivationPage() {
    const params = useParams();
    const agentId = (params['agent-id'] ?? '') as string;
    const {token, logout} = useAuth();
    const dispatch = useAppDispatch();
    const [agent, setAgent] = useState<AgentBasicDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const gcpUrl = useMemo(() => {
        if (!agent || !agent.projectId || !agent.location || !agent.gcpAgentId) return '';
        return `https://conversational-agents.cloud.google.com/projects/${agent.projectId}/locations/${agent.location}/agents/${agent.gcpAgentId}/(cx/integrations//right-panel:simulator)`;
    }, [agent]);

    const onOpenGcp = useCallback(() => {
        if (!gcpUrl) {
            dispatch(setSnackbarOpen({snackbarMessage: 'Fehlende Parameter in der URL.', snackbarSeverity: 'error'}));
            return;
        }
        window.open(gcpUrl, '_blank', 'noopener,noreferrer');
    }, [gcpUrl, dispatch]);

    const onActivate = useCallback(async () => {
        if (!token) return;
        if (!agentId) {
            dispatch(setSnackbarOpen({snackbarMessage: 'agent-id fehlt.', snackbarSeverity: 'error'}));
            return;
        }
        try {
            await activateMessengerIntegrationForAgent(agentId, token);
            dispatch(setSnackbarOpen({snackbarMessage: 'Kunden wurden informiert.', snackbarSeverity: 'success'}));
        } catch (error: any) {
            // If unauthorized, logout like other pages
            if (error?.response?.status === 401) {
                logout();
            } else {
                dispatch(setSnackbarOpen({
                    snackbarMessage: 'Fehler beim Informieren der Kunden.',
                    snackbarSeverity: 'error'
                }));
            }
        }
    }, [agentId, token, dispatch, logout]);

    useEffect(() => {
        const load = async () => {
            if (!agentId || !token) return;
            setLoading(true);
            try {
                const res = await getAgentBasic(agentId, token);
                setAgent(res.data as AgentBasicDto);
            } catch (error: any) {
                if (error?.response?.status === 401) {
                    logout();
                } else {
                    dispatch(setSnackbarOpen({
                        snackbarMessage: 'Fehler beim Laden des Agenten.',
                        snackbarSeverity: 'error'
                    }));
                }
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [agentId, token, dispatch, logout]);

    return (
        <Box p={3}>
            <Stack spacing={2}>
                <Typography variant="h5">Messenger-Aktivierung</Typography>
                <Paper variant="outlined">
                    <Table size="small">
                        <TableBody>
                            <TableRow>
                                <TableCell variant="head">Client Name</TableCell>
                                <TableCell>{loading ? '...' : (agent?.clientName ?? '-')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell variant="head">Agent Anzeigename</TableCell>
                                <TableCell>{loading ? '...' : (agent?.agentDisplayName ?? '-')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell variant="head">GCP Agent ID</TableCell>
                                <TableCell>{loading ? '...' : (agent?.gcpAgentId ?? '-')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>
                <Stack direction="row" spacing={2}>
                    <Button variant="contained" color="primary" onClick={onOpenGcp}>
                        GCP Messenger Integration
                    </Button>
                    <Button variant="outlined" color="secondary" onClick={onActivate} disabled={!token}>
                        Kunden informieren
                    </Button>
                </Stack>
            </Stack>
        </Box>
    );
}

export default MessengerActivationPage;
